/**
 * Datadog RUM action names, global-context keys, and a shared attribute builder for the matching /
 * JIT / late-joiner telemetry. Pure strings + a builder — no store or React imports — so the Redux
 * listener and the React hooks can all share them. Single source of truth for the taxonomy.
 *
 * Every value satisfies the `monitoring` `Action` template type (`${Namespace}.${string}`), so they
 * can be passed straight to `monitoring.trackAction`.
 */

export const NatteringTelemetry = {
  /** Emitted on every teleprompter round-state-machine transition (the matching funnel). */
  roundStateChanged: 'nattering.round.state_changed',
  /** Authoritative "joined the matching pool" (observes joinNatteringModal.accepted). */
  poolOptIn: 'nattering.pool.opt_in',
  /** Authoritative "left the matching pool" (observes joinNatteringModal.skipped). */
  poolSkip: 'nattering.pool.skip',
  /** Best-effort: emitted on tab close/hide while in the matching experience. */
  leave: 'nattering.leave',
} as const;

export const StageInviteTelemetry = {
  received: 'backstage.stage_invite.received',
  accepted: 'backstage.stage_invite.accepted',
  declined: 'backstage.stage_invite.declined',
  timedOut: 'backstage.stage_invite.timed_out',
  autoDeclinedBlocked: 'backstage.stage_invite.auto_declined_blocked',
} as const;

/** RUM global-context keys this telemetry sets/clears. */
export const TelemetryContext = {
  /** { joinPhase: 'broadcast' | 'nattering', joinedRoundStartTime? } — set once per event. */
  joinContext: 'joinContext',
  /** { startTime } — the meeting-global round key. */
  round: 'round',
  /** { roomId } — the 1:1 match room, set on join. */
  room: 'room',
  /** { flow: 'nattering' | 'stage_invite' } — attributes existing device telemetry to a flow. */
  deviceGate: 'deviceGate',
} as const;

/**
 * Round attributes attached to every matching action. `roundStartTime` (the server round-start
 * timestamp) is the meeting-global round key; the human ordinal is derived at query time.
 */
export const roundAttrs = (
  roundStartTime: number | undefined,
  extra?: Record<string, unknown>
): Record<string, unknown> => ({
  roundStartTime,
  ...extra,
});
